<template>
    <div :class="`short-text short-text-align-${align}`">
        <!-- Normal Mode -->
        <template v-if="!isSpecial">
            <label for="short-text" class="field-label">
                {{ fieldLabel }}
                <span v-if="isRequired" class="required-indicator">*</span>
            </label>
            <div class="input-container">
                <div :class="['input-wrapper', { 'error': shouldShowError, 'filled': shortTextValue }]">
                    <input 
                        type="text" 
                        id="short-text" 
                        name="short-text" 
                        v-model="shortTextValue" 
                        @input="emitValues"
                        @focus="onFocus"
                        @blur="onBlur"
                        :class="{ 'has-value': shortTextValue }"
                    />
                    <div class="input-border"></div>
                </div>
                <label for="short-text" :class="['input-label', { 'error': shouldShowError }]">{{ altLabel }}</label>
                <div v-if="shouldShowError" class="error-message">
                    {{ fieldLabel }} alanı zorunludur
                </div>
        </div>
        </template>
        
        <!-- Special Mode (Unchanged) -->
        <div v-if="isSpecial" class="special-input-wrapper">
            <input
                type="text"
                id="short-text-nano"
                name="short-text"
                v-model="shortTextValue"
                @input="emitValues"
                class="special-input"
                :placeholder="fieldLabel"
            />
        </div>
    </div>
</template>

<script>
export default {
    name: "FormShortText",
    props: {
        fieldLabel: {
            type: String,
            required: true,
        },
        align: {
            type: String,
            required: true,
        },
        altLabel: {
            type: String,
            required: true,
        },
        isRequired: {
            type: Boolean,
            default: false,
        },
        showError: {
            type: Boolean,
            default: false,
        },
        value: {
            type: [Object, String],
            default: () => ({ shortText: '' })
        },
        formId: {
            type: String,
            required: true,
        },
        order: {
            type: Number,
            required: true,
        },
        fieldId: {
            type: String,
            required: true,
        }
    },
    data() {
        const safeValue = (typeof this.value === 'object' && this.value !== null) ? this.value : {};
        return {
            shortTextValue: safeValue.shortText || '',
            isSpecial: false,
        };
    },
    computed: {
        hasValidationError() {
            return this.isRequired && (!this.shortTextValue || this.shortTextValue.trim() === '');
        },
        shouldShowError() {
            return this.showError && this.hasValidationError;
        }
    },
    watch: {
        value: {
            handler(newValue) {
                const safeValue = (typeof newValue === 'object' && newValue !== null) ? newValue : {};
                this.shortTextValue = safeValue.shortText || '';
            },
            deep: true
        },
        shortTextValue: {
            handler() {
                this.emitValues();
            }
        },
    },
    methods: {
        emitValues() {
            const shortTextData = {
                shortText: this.shortTextValue,
                fieldLabel: this.fieldLabel,
                altLabel: this.altLabel
            };
            
            console.log('🔍 ShortText validation:', {
                fieldId: this.fieldId,
                isRequired: this.isRequired,
                shortTextValue: this.shortTextValue,
                hasValidationError: this.hasValidationError,
                shouldShowError: this.shouldShowError
            });
            
            this.$emit("input", shortTextData);
            this.$emit("validate", {
                fieldId: this.fieldId,
                hasError: this.hasValidationError,
            });
            localStorage.setItem(`${this.formId}-${this.order}`, JSON.stringify(shortTextData));
        },
        loadValues() {
            const savedData = localStorage.getItem(`${this.formId}-${this.order}`);
            if (savedData) {
                const parsedData = JSON.parse(savedData);
                this.shortTextValue = parsedData.shortText;
            }
        },
        onFocus(event) {
            if (!this.isSpecial) {
                event.target.parentElement.classList.add('focused');
            }
        },
        onBlur(event) {
            if (!this.isSpecial) {
                event.target.parentElement.classList.remove('focused');
            }
        }
    },
    mounted() {
        console.log('🔴 ShortText mounted:', {
            fieldId: this.fieldId,
            isRequired: this.isRequired,
            value: this.value,
            shortTextValue: this.shortTextValue,
            fieldLabel: this.fieldLabel
        });
        
        const hasSlugValue = this.value && typeof this.value === "object" && this.value.shortText;

        if (!hasSlugValue) {
            this.loadValues();
        }
        
        this.emitValues();
        
        const special = this.$route.query.special;
        if (special === "true") {
            this.isSpecial = true;
        }
    }
};
</script>

<style lang="scss" scoped>
/* Special Mode Styles (Unchanged) */
.special-input-wrapper {
    position: relative;
    width: 123%;
    max-width: 400px;
    margin: 0 auto;
    padding-left: 18px;
}

.special-input {
    width: 80%;
    padding: 12px;
    font-size: 18px;
    border: none;
    border-radius: 20px;
    background: linear-gradient(90deg, #e2e4e4, #f7f7f7);
    color: #000;
    text-align: center;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease;
}

.special-input:focus {
    outline: none;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
    color: black;
    font-weight: bold;
    font-size: 14px;
}

.special-input::placeholder {
    color: black;
    font-weight: bold;
    font-size: 14px;
}

/* Base ShortText Styles (Normal Mode) */
.short-text {
    width: 100%;
    max-width: 700px;
    margin: clamp(0.5rem, 1.5vw, 1rem) auto;
    padding: clamp(0.625rem, 1.5vw, 0.875rem);
    position: relative;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    
    background: linear-gradient(145deg, 
        rgba(255, 255, 255, 0.02) 0%,
        rgba(255, 255, 255, 0.01) 100%);
    border-radius: 0.75rem;
    border: 1px solid rgba(255, 255, 255, 0.06);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    
    &:hover {
        background: linear-gradient(145deg, 
            rgba(255, 255, 255, 0.03) 0%,
            rgba(255, 255, 255, 0.02) 100%);
    }

    .field-label {
        display: block;
        font-size: clamp(0.95rem, 2.2vw, 1.1rem);
        font-weight: 600;
        margin-bottom: clamp(0.5rem, 1.2vw, 0.75rem);
        color: var(--text-primary, rgba(55, 65, 81, 0.9));
        line-height: 1.4;
        letter-spacing: -0.01em;
        
        .required-indicator {
            color: #ef4444;
            margin-left: 0.125rem;
            font-weight: 700;
        }
    }

    .input-container {
        position: relative;
        min-width: 0; // Flex item için minimum genişlik
    }

    .input-wrapper {
        position: relative;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

        input {
            width: 100%;
            padding: clamp(0.6rem, 1.5vw, 0.75rem) clamp(0.6rem, 1.2vw, 0.75rem);
            font-size: clamp(0.875rem, 1.8vw, 0.9rem);
            line-height: 1.4;
            color: var(--text-primary, rgba(55, 65, 81, 0.9));
            background: rgba(255, 255, 255, 0.8);
            border: 1px solid rgba(209, 213, 219, 0.6);
            border-radius: 0.375rem;
            box-sizing: border-box;
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            font-family: inherit;
            backdrop-filter: blur(10px);
            
            &::placeholder {
                color: rgba(107, 114, 128, 0.6);
            }
            
            &:focus {
                outline: none;
                border-color: var(--primary, #6366f1);
                background: rgba(255, 255, 255, 0.95);
                box-shadow: 
                    0 0 0 3px rgba(99, 102, 241, 0.1),
                    0 4px 12px rgba(0, 0, 0, 0.05);
                transform: translateY(-1px);
            }
            
            &:hover:not(:focus) {
                border-color: rgba(107, 114, 128, 0.4);
                background: rgba(255, 255, 255, 0.85);
            }
        }

        .input-border {
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            height: 2px;
            background: linear-gradient(90deg, var(--primary, #6366f1), var(--secondary, #8b5cf6));
            border-radius: 1px;
            transform: scaleX(0);
            transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        &.focused .input-border,
        input:focus ~ .input-border {
            transform: scaleX(1);
        }

        &.error {
            input {
                border-color: #ef4444;
                background: rgba(254, 242, 242, 0.8);
                
                &:focus {
                    border-color: #ef4444;
                    box-shadow: 
                        0 0 0 3px rgba(239, 68, 68, 0.1),
                        0 4px 12px rgba(239, 68, 68, 0.1);
                }
            }
            
            .input-border {
                background: #ef4444;
            }
        }
    }

    .input-label {
        display: block;
        font-size: clamp(0.75rem, 1.6vw, 0.8rem);
        color: rgba(107, 114, 128, 0.8);
        margin-top: clamp(0.25rem, 0.8vw, 0.375rem);
        font-weight: 500;
        transition: color 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        
        &.error {
            color: #ef4444;
        }
    }

    .error-message {
        color: #ef4444;
        font-size: clamp(0.8rem, 1.8vw, 0.875rem);
        margin-top: clamp(0.375rem, 1vw, 0.5rem);
        font-weight: 500;
        display: flex;
        align-items: center;
        gap: 0.375rem;
        
        &::before {
            content: '⚠';
            font-size: 0.875em;
        }
    }
}

/* Left Alignment - Label solda, input sağda */
.short-text-align-left {
    display: flex;
    align-items: flex-start;
    gap: clamp(0.75rem, 2vw, 1rem);
    text-align: left;

    .field-label {
        min-width: clamp(100px, 20vw, 140px);
        flex-shrink: 0;
        margin-bottom: 0;
        padding-top: clamp(0.6rem, 1.5vw, 0.75rem);
        text-align: left;
    }

    .input-container {
        flex: 1;
    }
    
    @media (max-width: 768px) {
        flex-direction: column;
        gap: 0;
        
        .field-label {
            min-width: auto;
            padding-top: 0;
            margin-bottom: clamp(0.5rem, 1.2vw, 0.75rem);
    }

        .input-container {
        width: 100%;
        }
    }
}

/* Right Alignment - Label solda sağa yaslanmış, input sağda */
.short-text-align-right {
    display: flex;
    align-items: flex-start;
    gap: clamp(0.75rem, 2vw, 1rem);

    .field-label {
        min-width: clamp(100px, 20vw, 140px);
        flex-shrink: 0;
        margin-bottom: 0;
        padding-top: clamp(0.6rem, 1.5vw, 0.75rem);
        text-align: right;
    }

    .input-container {
        flex: 1;
    }
    
    @media (max-width: 768px) {
        flex-direction: column;
        gap: 0;
        
        .field-label {
            min-width: auto;
            padding-top: 0;
            margin-bottom: clamp(0.5rem, 1.2vw, 0.75rem);
            text-align: left;
    }

        .input-container {
        width: 100%;
    }
}
}

/* Center/Top Alignment (Default) */
.short-text-align-top {
    text-align: left;
    flex-direction: column;
}

// Mobil optimizasyonları
@media (max-width: 768px) {
    .short-text {
        margin: clamp(0.25rem, 1vw, 0.5rem) auto;
        padding: clamp(0.5rem, 1.2vw, 0.75rem);
        
        .field-label {
            font-size: clamp(0.9rem, 2vw, 1rem);
            margin-bottom: clamp(0.375rem, 1vw, 0.5rem);
    }

    .input-wrapper {
            input {
                padding: clamp(0.75rem, 2vw, 0.875rem) clamp(0.625rem, 1.5vw, 0.75rem);
                font-size: clamp(0.875rem, 2vw, 0.9rem);
                border-radius: 0.5rem;
            }
            
            .input-label {
                font-size: clamp(0.75rem, 1.8vw, 0.8rem);
                margin-top: clamp(0.25rem, 0.8vw, 0.375rem);
        }
        }
        
        .error-message {
            font-size: clamp(0.8rem, 1.8vw, 0.85rem);
            margin-top: clamp(0.25rem, 0.8vw, 0.375rem);
        }
    }
}

@media (max-width: 480px) {
    .short-text {
        margin: 0.25rem auto;
        padding: 0.5rem;
        
        .input-wrapper {
            input {
                padding: 0.75rem 0.625rem;
                font-size: 0.875rem;
            }
            
            .input-label {
                font-size: 0.75rem;
                margin-top: 0.25rem;
            }
        }

        .error-message {
            font-size: 0.8rem;
            margin-top: 0.25rem;
        }
    }
}

/* Dark mode devre dışı - Her zaman açık tema kullan */

/* Reduced Motion Support */
@media (prefers-reduced-motion: reduce) {
    .short-text,
    .input-wrapper,
    .input-wrapper input,
    .input-label,
    .input-border {
        transition: none;
    }
    
    .input-wrapper input:focus {
        transform: none;
    }
}

/* High Performance Mode */
.input-wrapper {
    will-change: transform;
    
    input {
        will-change: transform, box-shadow;
    }
    
    .input-border {
        will-change: transform;
        }
    }

.input-label {
    will-change: color;
}
</style>